import { useState } from "react";
import { toast } from "sonner";

/**
 * Reusable lead-capture form used by Book, Zoom, and Full-results flows.
 * fields: array of { name, label, type, required, placeholder }
 */
export default function LeadForm({
  fields,
  submitLabel,
  onSubmit,
  consentLabel,
  testIdPrefix,
  extraNote,
  successContent,
}) {
  const initial = fields.reduce((acc, f) => ({ ...acc, [f.name]: "" }), {});
  const [values, setValues] = useState(initial);
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [response, setResponse] = useState(null);

  const handle = async (e) => {
    e.preventDefault();
    for (const f of fields) {
      if (f.required && !values[f.name]?.trim()) {
        toast.error(`Please enter your ${f.label.toLowerCase()}.`);
        return;
      }
    }
    if (!consent) {
      toast.error("Please check the consent box to continue.");
      return;
    }
    setLoading(true);
    try {
      const res = await onSubmit({ ...values, consent });
      toast.success(res?.message || "Thank you — we've received your details.");
      setResponse(res);
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="text-center py-6" data-testid={`${testIdPrefix}-success`}>
        <div className="mx-auto grid place-items-center h-14 w-14 rounded-full bg-[#EAF2EC] mb-4">
          <i className="fa-solid fa-check text-[#4A7C59] text-xl" />
        </div>
        <h3 className="font-playfair text-xl text-stone-900">You're all set</h3>
        <p className="text-stone-600 text-sm mt-2 max-w-sm mx-auto">
          We've received your details and sent a confirmation to your email — please check your
          inbox (and spam folder, just in case).
        </p>
        {successContent && <div className="mt-6">{successContent(response)}</div>}
      </div>
    );
  }

  return (
    <form onSubmit={handle} className="space-y-4" data-testid={`${testIdPrefix}-form`}>
      {fields.map((f) => (
        <div key={f.name}>
          <label className="block text-sm font-medium text-stone-700 mb-1.5">
            {f.label}
            {f.required && <span className="text-[#1F3D2E]"> *</span>}
          </label>
          {f.type === "textarea" ? (
            <textarea
              data-testid={`${testIdPrefix}-${f.name}`}
              value={values[f.name]}
              onChange={(e) => setValues((v) => ({ ...v, [f.name]: e.target.value }))}
              placeholder={f.placeholder}
              rows={4}
              className="w-full rounded-xl border border-stone-300 bg-white px-4 py-3 text-sm text-stone-800 focus:outline-none focus:ring-2 focus:ring-[#1F3D2E]/40"
            />
          ) : (
            <input
              data-testid={`${testIdPrefix}-${f.name}`}
              type={f.type || "text"}
              value={values[f.name]}
              onChange={(e) => setValues((v) => ({ ...v, [f.name]: e.target.value }))}
              placeholder={f.placeholder}
              className="w-full rounded-xl border border-stone-300 bg-white px-4 py-3 text-sm text-stone-800 focus:outline-none focus:ring-2 focus:ring-[#1F3D2E]/40"
            />
          )}
        </div>
      ))}

      <label className="flex items-start gap-3 text-sm text-stone-600">
        <input
          type="checkbox"
          data-testid={`${testIdPrefix}-consent`}
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-1 accent-[#1F3D2E] h-4 w-4"
        />
        <span>{consentLabel}</span>
      </label>

      {extraNote}

      <button
        type="submit"
        disabled={loading}
        data-testid={`${testIdPrefix}-submit`}
        className="btn-terracotta w-full rounded-full px-6 py-3.5 text-sm font-semibold disabled:opacity-60"
      >
        {loading ? "Sending…" : submitLabel}
      </button>
    </form>
  );
}
